import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SchemaOrg type="WebPage" data={{
        name: `${category.name} - 强奸罪法律解析与辩护指南`,
        description: category.description,
        url: typeof window !== 'undefined' ? window.location.href : '',
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '' },
          { '@type': 'ListItem', position: 2, name: category.name },
        ],
      }} />

      <Navbar />
      <main className="pt-20">
        <Breadcrumb items={[{ label: category.name }]} />

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{category.name}</h1>
                <p className="text-sm text-foreground-500">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category tabs */}
        <section className="py-3 bg-background-100/70 border-b border-background-200 sticky top-16 z-30">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="space-y-4">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="block p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h2 className="font-heading font-semibold text-foreground-950 text-sm md:text-base mb-2 hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{article.publishedAt}</span>
                        <span>·</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-1 text-foreground-300 flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}